import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import {
  Globe,
  Users,
  Eye,
  FileText,
  Link2,
  Megaphone,
  MapPin,
  Monitor,
  Clock,
  TrendingUp,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  Video,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// ---------------------------------------------------------------------------
// Types (mirrors backend response)
// ---------------------------------------------------------------------------

interface SectionResult<T> {
  data: T | null;
  error: string | null;
}

interface VisitorTrendPoint {
  date: string;
  visitors: number;
  pageviews: number;
}

interface TopPage {
  path: string;
  views: number;
  uniqueVisitors: number;
}

interface ReferralSource {
  domain: string;
  visitors: number;
}

interface UtmCampaign {
  campaign: string;
  source: string;
  medium: string;
  visitors: number;
}

interface GeoEntry {
  name: string;
  visitors: number;
}

interface DeviceEntry {
  type: string;
  count: number;
}

interface SessionKpis {
  sessions: number;
  avgDurationSeconds: number;
  bounceRate: number;
}

interface WebsiteAnalyticsResponse {
  visitorTrend: SectionResult<VisitorTrendPoint[]>;
  topPages: SectionResult<TopPage[]>;
  referrals: SectionResult<ReferralSource[]>;
  utmCampaigns: SectionResult<UtmCampaign[]>;
  countries: SectionResult<GeoEntry[]>;
  cities: SectionResult<GeoEntry[]>;
  devices: SectionResult<DeviceEntry[]>;
  sessionKpis: SectionResult<SessionKpis>;
  posthogProjectId: string;
  period: string;
  websiteHost: string;
  generatedAt: string;
}

type Period = '7d' | '30d' | '90d';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function SectionError({ error }: { error: string }) {
  return (
    <div className="flex items-center gap-2 p-4 rounded-lg bg-red-900/20 border border-red-700/30">
      <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
      <p className="text-sm text-red-300">Failed to load: {error}</p>
    </div>
  );
}

function BarList({
  items,
  color = 'bg-blue-500',
}: {
  items: { label: string; value: number }[];
  color?: string;
}) {
  const max = items.length > 0 ? items[0].value : 1;
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const pct = (item.value / max) * 100;
        return (
          <div key={item.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-200 truncate mr-3">{item.label}</span>
              <span className="text-sm font-semibold text-white whitespace-nowrap">
                {item.value.toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-1.5">
              <div
                className={`${color} h-1.5 rounded-full transition-all duration-300`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
      {items.length === 0 && (
        <p className="text-sm text-gray-500">No data available</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WebsiteAnalyticsTab() {
  const [data, setData] = useState<WebsiteAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState<Period>('30d');

  const fetchData = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      else setLoading(true);

      const fn = httpsCallable<{ period: string }, WebsiteAnalyticsResponse>(
        functions,
        'getWebsiteAnalytics'
      );
      const result = await fn({ period });
      setData(result.data);
      if (showToast) toast.success('Website analytics refreshed');
    } catch (err: any) {
      console.error('Error fetching website analytics:', err);
      toast.error(err.message || 'Failed to load website analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          <p className="text-white text-lg">Loading website analytics...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-400">
          Failed to load website analytics. Check PostHog configuration.
        </p>
      </div>
    );
  }

  // Computed totals from visitor trend
  const trend = data.visitorTrend.data || [];
  const totalVisitors = trend.reduce((s, d) => s + d.visitors, 0);
  const totalPageviews = trend.reduce((s, d) => s + d.pageviews, 0);
  const kpis = data.sessionKpis.data;

  return (
    <div className="space-y-6">
      {/* Period selector + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2 p-1 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50">
          {(['7d', '30d', '90d'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                period === p
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
              }`}
            >
              {p === '7d' ? 'Last 7 days' : p === '30d' ? 'Last 30 days' : 'Last 90 days'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`https://eu.posthog.com/project/${data.posthogProjectId}/replay?filter_test_accounts=false`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 rounded-lg transition-all text-slate-300 text-sm"
          >
            <Video className="w-4 h-4" />
            Recordings
            <ExternalLink className="w-3 h-3" />
          </a>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 rounded-lg transition-all disabled:opacity-50 text-slate-300"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 border border-blue-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{totalVisitors.toLocaleString()}</div>
          <div className="text-blue-100 text-sm">Unique Visitors</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-blue-100">{data.websiteHost}</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 border border-purple-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Eye className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{totalPageviews.toLocaleString()}</div>
          <div className="text-purple-100 text-sm">Total Pageviews</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-purple-100">
              {totalVisitors > 0 ? (totalPageviews / totalVisitors).toFixed(1) : '0'} per visitor
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-6 border border-green-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">
            {kpis ? kpis.sessions.toLocaleString() : '—'}
          </div>
          <div className="text-green-100 text-sm">Sessions</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-green-100">
              {kpis ? `Avg ${formatDuration(kpis.avgDurationSeconds)}` : 'N/A'}
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl p-6 border border-orange-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">
            {kpis ? `${kpis.bounceRate}%` : '—'}
          </div>
          <div className="text-orange-100 text-sm">Bounce Rate</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-orange-100">
              {kpis
                ? kpis.bounceRate <= 40
                  ? 'Excellent'
                  : kpis.bounceRate <= 60
                    ? 'Average'
                    : 'Needs work'
                : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Visitor Trend Chart */}
      {data.visitorTrend.error ? (
        <SectionError error={data.visitorTrend.error} />
      ) : trend.length > 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Visitor Trend</h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="visitorsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="pageviewsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(val) => {
                    const d = new Date(val);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }}
                />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    color: '#f1f5f9',
                  }}
                  labelFormatter={(val) => new Date(val).toLocaleDateString('en-AU')}
                />
                <Area
                  type="monotone"
                  dataKey="visitors"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#visitorsGrad)"
                  name="Visitors"
                />
                <Area
                  type="monotone"
                  dataKey="pageviews"
                  stroke="#a855f7"
                  strokeWidth={2}
                  fill="url(#pageviewsGrad)"
                  name="Pageviews"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}

      {/* Top Pages + Referral Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-6 h-6 text-indigo-400" />
            <h2 className="text-xl font-semibold text-white">Top Pages</h2>
          </div>
          {data.topPages.error ? (
            <SectionError error={data.topPages.error} />
          ) : (
            <BarList
              items={(data.topPages.data || []).map((p) => ({
                label: p.path,
                value: p.views,
              }))}
              color="bg-indigo-500"
            />
          )}
        </div>

        {/* Referral Sources */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Link2 className="w-6 h-6 text-green-400" />
            <h2 className="text-xl font-semibold text-white">Referral Sources</h2>
          </div>
          {data.referrals.error ? (
            <SectionError error={data.referrals.error} />
          ) : (
            <BarList
              items={(data.referrals.data || []).map((r) => ({
                label: r.domain,
                value: r.visitors,
              }))}
              color="bg-green-500"
            />
          )}
        </div>
      </div>

      {/* UTM Campaigns */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Megaphone className="w-6 h-6 text-amber-400" />
          <h2 className="text-xl font-semibold text-white">UTM Campaigns</h2>
        </div>
        {data.utmCampaigns.error ? (
          <SectionError error={data.utmCampaigns.error} />
        ) : (data.utmCampaigns.data || []).length === 0 ? (
          <p className="text-sm text-gray-500">No UTM-tagged campaigns detected</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 font-medium py-3 pr-4">Campaign</th>
                  <th className="text-left text-gray-400 font-medium py-3 px-3">Source</th>
                  <th className="text-left text-gray-400 font-medium py-3 px-3">Medium</th>
                  <th className="text-right text-gray-400 font-medium py-3 pl-3">Visitors</th>
                </tr>
              </thead>
              <tbody>
                {(data.utmCampaigns.data || []).map((c, i) => (
                  <tr
                    key={`${c.campaign}-${c.source}-${c.medium}-${i}`}
                    className="border-b border-gray-700/50 hover:bg-gray-700/20"
                  >
                    <td className="py-3 pr-4 text-white">{c.campaign}</td>
                    <td className="py-3 px-3 text-gray-300">{c.source}</td>
                    <td className="py-3 px-3 text-gray-300">{c.medium}</td>
                    <td className="py-3 pl-3 text-right text-white font-semibold">
                      {c.visitors.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Geography: Countries + Cities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Globe className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Countries</h2>
          </div>
          {data.countries.error ? (
            <SectionError error={data.countries.error} />
          ) : (
            <BarList
              items={(data.countries.data || []).map((c) => ({
                label: c.name,
                value: c.visitors,
              }))}
              color="bg-blue-500"
            />
          )}
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-6 h-6 text-purple-400" />
            <h2 className="text-xl font-semibold text-white">Cities</h2>
          </div>
          {data.cities.error ? (
            <SectionError error={data.cities.error} />
          ) : (
            <BarList
              items={(data.cities.data || []).map((c) => ({
                label: c.name,
                value: c.visitors,
              }))}
              color="bg-purple-500"
            />
          )}
        </div>
      </div>

      {/* Devices */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Monitor className="w-6 h-6 text-teal-400" />
          <h2 className="text-xl font-semibold text-white">Devices</h2>
        </div>
        {data.devices.error ? (
          <SectionError error={data.devices.error} />
        ) : (
          <BarList
            items={(data.devices.data || []).map((d) => ({
              label: d.type,
              value: d.count,
            }))}
            color="bg-teal-500"
          />
        )}
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-500">
        Data from PostHog &middot; {data.websiteHost} &middot; Generated{' '}
        {new Date(data.generatedAt).toLocaleString('en-AU')}
      </div>
    </div>
  );
}
